import type { ReactNode } from 'react';
import { Card, PageErrorBoundary } from '@citizen-walk/shared-ui';
import { BrowserRouter, Link, Route, Routes, useParams } from 'react-router-dom';
import { captureException } from './lib/sentry';
import {
  AdminAuthGate,
  AuthActionPage,
  AuthInvitePage,
  type AdminAuthContext,
} from './features/auth';
import { AdminLayout } from './components/AdminLayout';
import {
  DistrictAiSettingsPage,
  DistrictDetailPage,
  DistrictDisplayPage,
  DistrictMiscPage,
  DistrictsAdminPage,
  MapPmtilesInspectorPage,
  useDistricts,
} from './features/districts-admin';
import { DataSettingsPage } from './features/data-settings';
import {
  AiAnalysisEditorPage,
  AiCategoryMastersPage,
  AiDialogueSettingsPage,
  AiOutputFormatsPage,
  StreetSurveyTemplatesPage,
  AnalysisDisplayConfigsPage,
  CardPrintTemplatesPage,
  ModeProfilesPage,
  ScoreDimensionMastersPage,
  SurveyPresetEditPage,
  SurveyPresetsPage,
  VerificationTemplatePresetEditPage,
  VerificationTemplatePresetsPage,
} from './features/masters';
import { AnnouncementsAdminPage } from './features/announcements-admin';
import { CardPrintPage } from './features/card-print';
import { ExportPage } from './features/export';
import { GisAdminPage } from './features/gis-admin';
import { AdminGuidePage } from './features/help';
import { ImportPage } from './features/import';
import { BulkPhotoImportPage } from './features/bulk-photo-import';
import { McpTokensPage } from './features/mcp-tokens';
import { InsightsModerationPage } from './features/insights-moderation';
import { OperationLogsPage } from './features/operation-logs';
import { PostsModerationPage } from './features/posts-moderation';
import { SessionsAdminPage } from './features/sessions-admin';
import { ParticipantsAdminPage } from './features/participants-admin';
import {
  AreaReportDashboardPage,
  RouteReportDashboardPage,
  StreetSurveyDetailPage,
  StreetSurveyPage,
} from './features/street-survey';
import { SurveyEditPage, SurveyResponsesPage, SurveysAdminPage } from './features/survey-admin';
import { UsersAdminPage } from './features/users-admin';
import {
  AssignmentSetGeneratorPage,
  AssignmentPlanEditPage,
  VerificationAssignmentEditPage,
  VerificationAssignmentsPage,
  VerificationResultsPage,
  VerificationTemplateEditPage,
  VerificationTemplatesAdminPage,
} from './features/verification-admin';

/**
 * CitizenWalk Admin (Phase 5 完了済 + サイドバーレイアウト統一)。
 *
 * 旧 `old/apps/admin/src/App.tsx` のサイドバー + 階層ナビ構造を踏襲。
 * HomePage はダッシュボードとして「今日のやること」を一覧表示する役割に再定義。
 */

interface DashboardLink {
  label: string;
  to: string;
  description: string;
}

/**
 * Sprint BG-Menu (第 1 段 + 第 2 段): HomePage のカード集も role 別に再編成。
 * 旧「M1-M10」番号表示は廃止、日本語名で分かりやすく統一。
 * 説明文は開発者ジャーゴンを排した平易な日本語に統一 (START GUIDE 再設計)。
 */

const TEMPLATE_LINKS: DashboardLink[] = [
  {
    label: 'AI 解析 統合エディタ',
    to: '/masters/ai-analysis-profile',
    description: 'AIの解析設定・カテゴリ・出力フォーマットを1画面で編集（プレビュー付き）',
  },
  { label: 'AI カテゴリ', to: '/masters/ai-category', description: 'AI 分類のカテゴリ' },
  {
    label: 'AI 出力フォーマット',
    to: '/masters/ai-output-format',
    description: 'AIが返す解析結果の項目構成',
  },
  {
    label: 'AI 対話設定',
    to: '/masters/ai-dialogue-settings',
    description: 'AIとの対話モードの問いかけ文',
  },
  {
    label: '机上調査テンプレート',
    to: '/masters/street-survey-templates',
    description:
      '机上調査（ストリートビュー / 360 度写真）の解析の設定と文面を地区に適用するテンプレート',
  },
  {
    label: '分析表示設定',
    to: '/masters/analysis-display',
    description: '分析画面での投稿詳細の表示テンプレート',
  },
  // 【廃止予定 / MD-3 で非表示化 2026-07-24】M6 スコア軸定義 (ScoreDimensionMastersPage) は
  // runtime 読込み箇所が存在せず (監査で dead 判定)、管理者の混乱を避けるため nav から除去。
  // データ・型・route 実装は温存 — M5 postDetailScores への配線 or 撤去の判断は issue 参照。
  // { label: 'スコア軸定義', to: '/masters/score-dimension', description: '評価スコアの軸' },
  { label: 'カード印刷テンプレ', to: '/masters/card-print', description: 'カード印刷のレイアウト' },
  {
    label: 'アンケートテンプレ',
    to: '/masters/survey-preset',
    description: 'アンケートのテンプレート',
  },
  {
    label: '検証テンプレ',
    to: '/masters/verification-template',
    description: 'AI解析結果を参加者に検証してもらうテンプレート',
  },
  {
    label: 'モードプロファイル',
    to: '/masters/mode-profiles',
    description: '新しい地区を作るときの初期の入力項目とおすすめ設定',
  },
];

const AUDIT_LINKS: DashboardLink[] = [
  {
    // 地区の「ユーザー管理」(参加者の登録データ) と紛らわしいため、2026-09-07 に
    // 「管理者アカウント」へ改称 (画面の中身は管理者の招待・権限設定で変わらない)。
    label: '管理者アカウント',
    to: '/users',
    description: '管理者の招待と権限（マスター／地区）・担当地区の設定',
  },
  {
    label: '操作ログ',
    to: '/operation-logs',
    description: 'お知らせ・セッションなどの操作履歴',
  },
];

const HELP_LINK: DashboardLink = {
  label: '使い方ガイド',
  to: '/help',
  description: '管理画面の主な機能のクイックガイド',
};

/** 外部 AI 連携 (MCP) — master / 地区管理者の両方が自分の担当地区分を発行できる。 */
const MCP_LINK: DashboardLink = {
  label: '外部 AI 連携（MCP）',
  to: '/mcp-tokens',
  description: 'Claude/ChatGPT/Gemini から地区データを分析するアクセストークンを発行',
};

function DashboardCardList({ links }: { links: DashboardLink[] }) {
  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {links.map((l) => (
        <li key={l.to}>
          <Link to={l.to} className="block group">
            <Card padding="md" className="h-full hover:border-primary-300 transition-colors">
              <p className="text-sm font-semibold text-slate-900 group-hover:text-primary-700">
                {l.label}
              </p>
              <p className="mt-1 text-xs text-slate-600">{l.description}</p>
            </Card>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * START GUIDE: 初めての管理者向けの基本フロー (4 ステップ)。
 * 1 のみマスター管理者が地区を作る前提でリンクを置き、2〜4 は地区選択後に各地区メニューで行う旨を明記。
 */
function StartGuideSection() {
  return (
    <section className="mb-8">
      <h2 className="text-base font-bold text-slate-900 mb-3">はじめての方へ（基本の流れ）</h2>
      <Card padding="md">
        <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
          <li>
            地区をつくる（マスター管理者）—{' '}
            <Link
              to="/districts"
              className="text-primary-600 hover:text-primary-700 hover:underline"
            >
              地区の管理を開く
            </Link>
            。担当地区がある方は下の「担当地区」から選んでください。
          </li>
          <li>入力項目を決める（参加者登録・投稿フォームの項目）</li>
          <li>参加用の URL・QRコードを配布する</li>
          <li>投稿を確認・モデレーションする</li>
        </ol>
        <p className="mt-3 text-xs text-slate-500">
          2〜4 は地区を選んでから、その地区のメニューで行います。
        </p>
      </Card>
    </section>
  );
}

/**
 * 担当地区サマリ (両 role 共通)。
 * アクセス可能な地区 (master = 全地区 / district admin = ctx.districts に含まれる地区) を
 * 概要ページ (/districts/{id}) へのカードリンクで並べる。master は地区一覧へのリンクも添える。
 * 0 件のときは短いヒントを表示する。
 */
function AssignedDistrictsSection({ ctx }: { ctx: AdminAuthContext }) {
  const isMaster = ctx.role === 'master';
  const districtsState = useDistricts();

  let body: ReactNode;
  if (districtsState.status === 'loading') {
    body = <p className="text-sm text-slate-500">地区を読み込み中です…</p>;
  } else if (districtsState.status === 'error') {
    body = <p className="text-sm text-rose-600">地区の読み込みに失敗しました。</p>;
  } else {
    const allow = new Set(ctx.districts);
    const myDistricts = isMaster
      ? districtsState.items
      : districtsState.items.filter((d) => allow.has(d.id));

    if (myDistricts.length === 0) {
      body = (
        <p className="text-sm text-slate-500">
          {isMaster
            ? 'まだ地区がありません。上の「地区をつくる」から最初の地区を作成してください。'
            : '担当地区がまだ割り当てられていません。マスター管理者にお問い合わせください。'}
        </p>
      );
    } else {
      const links: DashboardLink[] = myDistricts.map((d) => ({
        label: d.name,
        to: `/districts/${d.id}`,
        description: 'この地区の概要・各メニューを開く',
      }));
      body = <DashboardCardList links={links} />;
    }
  }

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-slate-900">担当地区</h2>
        {isMaster && (
          <Link
            to="/districts"
            className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
          >
            地区一覧
          </Link>
        )}
      </div>
      {body}
    </section>
  );
}

/**
 * Phase 4: 地区モードの AI 解析統合エディタ route wrapper。
 * route param :districtId を読み、AiAnalysisEditorPage を地区モード (districtId 付き) で開く。
 * uid は master uid 経路 (ctx.user?.uid、テンプレモードと同様)。
 */
function DistrictAiEditorRoute({ ctx }: { ctx: AdminAuthContext }) {
  const { districtId } = useParams<{ districtId: string }>();
  if (!districtId) return null;
  return <AiAnalysisEditorPage districtId={districtId} uid={ctx.user?.uid} />;
}

function HomePage({ ctx }: { ctx: AdminAuthContext }) {
  const isMaster = ctx.role === 'master';
  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">ダッシュボード</h1>
        <p className="text-sm text-slate-500 mt-1">
          初めての方は「はじめての方へ（基本の流れ）」をご覧ください。担当する地区は下の「担当地区」から開けます。
        </p>
      </header>

      <StartGuideSection />

      <AssignedDistrictsSection ctx={ctx} />

      <section className="mb-8">
        <h2 className="text-base font-bold text-slate-900 mb-3">外部 AI 連携</h2>
        <DashboardCardList links={[MCP_LINK]} />
      </section>

      {isMaster && (
        <section className="mb-8">
          <h2 className="text-base font-bold text-slate-900 mb-3">テンプレ・マスタ</h2>
          <DashboardCardList links={TEMPLATE_LINKS} />
        </section>
      )}

      {isMaster && (
        <section className="mb-8">
          <h2 className="text-base font-bold text-slate-900 mb-3">権限・監査</h2>
          <DashboardCardList links={AUDIT_LINKS} />
        </section>
      )}

      {isMaster && (
        <section className="mb-8">
          <h2 className="text-base font-bold text-slate-900 mb-3">使い方ガイド</h2>
          <DashboardCardList links={[HELP_LINK]} />
        </section>
      )}
    </>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* 公開ルート: 管理者招待 / PW 再発行リンクの着地点。未認証で踏むため AuthGate の外に置く
            (verify→PW設定→自動ログイン→ホーム まで自己完結)。
            - /auth/action : Firebase 既定 oobCode 用 (セルフ「お忘れの方」、Console action URL 設定時)。
            - /auth/invite : マスター発行の独自招待トークン用 (期限指定可、AuthInvitePage)。 */}
        <Route path="/auth/action" element={<AuthActionPage />} />
        <Route path="/auth/invite" element={<AuthInvitePage />} />
        <Route
          path="/*"
          element={
            <AdminAuthGate>
              {(ctx) => (
                <AdminLayout ctx={ctx}>
                  <PageErrorBoundary onError={(err) => captureException(err)}>
                    <Routes>
                      <Route path="/" element={<HomePage ctx={ctx} />} />
                      <Route path="/districts" element={<DistrictsAdminPage />} />
                      <Route path="/districts/:districtId" element={<DistrictDetailPage />} />
                      {/* Sprint BG-Menu (第 3 段): DistrictDetailPage 分割 */}
                      <Route
                        path="/districts/:districtId/display"
                        element={<DistrictDisplayPage />}
                      />
                      <Route
                        path="/districts/:districtId/ai"
                        element={<DistrictAiSettingsPage />}
                      />
                      {/* Phase 4: 地区モード統合エディタ (テンプレ継承グレー + 項目ごと上書きトグル) */}
                      <Route
                        path="/districts/:districtId/ai-editor"
                        element={<DistrictAiEditorRoute ctx={ctx} />}
                      />
                      <Route path="/districts/:districtId/misc" element={<DistrictMiscPage />} />
                      <Route
                        path="/districts/:districtId/data-settings"
                        element={<DataSettingsPage />}
                      />
                      {/* Sprint G2 (docs/22 GIS): 地区 GIS データ管理 (master + 担当 district admin) */}
                      <Route path="/districts/:districtId/gis" element={<GisAdminPage />} />
                      {/* AW10 (docs/108 Critical #15): PMTiles 検査ツール (live MapLibre インスペクタ) */}
                      <Route
                        path="/districts/:districtId/pmtiles-inspector"
                        element={<MapPmtilesInspectorPage />}
                      />
                      <Route
                        path="/districts/:districtId/moderation"
                        element={<PostsModerationPage />}
                      />
                      {/* docs/25 ストリートビュー机上調査 (Increment 2) */}
                      <Route
                        path="/districts/:districtId/street-survey"
                        element={<StreetSurveyPage />}
                      />
                      <Route
                        path="/districts/:districtId/street-survey/:postId"
                        element={<StreetSurveyDetailPage />}
                      />
                      <Route
                        path="/districts/:districtId/street-survey/area-reports"
                        element={<AreaReportDashboardPage />}
                      />
                      <Route
                        path="/districts/:districtId/street-survey/route-reports"
                        element={<RouteReportDashboardPage />}
                      />
                      <Route path="/districts/:districtId/export" element={<ExportPage />} />
                      <Route
                        path="/districts/:districtId/export/cards"
                        element={<CardPrintPage />}
                      />
                      <Route path="/districts/:districtId/import" element={<ImportPage />} />
                      {/* issue #23 — 写真まとめて一括投稿ツール */}
                      <Route
                        path="/districts/:districtId/photo-import"
                        element={<BulkPhotoImportPage />}
                      />
                      <Route
                        path="/districts/:districtId/announcements"
                        element={<AnnouncementsAdminPage />}
                      />
                      <Route
                        path="/districts/:districtId/sessions"
                        element={<SessionsAdminPage />}
                      />
                      {/* ユーザー管理 (2026-09-07)。地区管理者に開くかは master が地区ごとに設定する
                          (ページ内で判定し、サーバ側 callable でも同じ判定を行う)。 */}
                      <Route
                        path="/districts/:districtId/participants"
                        element={<ParticipantsAdminPage />}
                      />
                      <Route path="/districts/:districtId/surveys" element={<SurveysAdminPage />} />
                      <Route
                        path="/districts/:districtId/surveys/:surveyId"
                        element={<SurveyEditPage />}
                      />
                      <Route
                        path="/districts/:districtId/surveys/:surveyId/responses"
                        element={<SurveyResponsesPage />}
                      />
                      <Route
                        path="/districts/:districtId/verification/templates"
                        element={<VerificationTemplatesAdminPage />}
                      />
                      <Route
                        path="/districts/:districtId/verification/templates/:templateId"
                        element={<VerificationTemplateEditPage />}
                      />
                      <Route
                        path="/districts/:districtId/verification-results"
                        element={<VerificationResultsPage />}
                      />
                      <Route
                        path="/districts/:districtId/verification/distribute"
                        element={<AssignmentSetGeneratorPage />}
                      />
                      <Route
                        path="/districts/:districtId/verification/assignmentSets"
                        element={<VerificationAssignmentsPage />}
                      />
                      <Route
                        path="/districts/:districtId/verification/assignmentSets/new"
                        element={<VerificationAssignmentEditPage />}
                      />
                      <Route
                        path="/districts/:districtId/verification/assignmentSets/:setId/edit"
                        element={<VerificationAssignmentEditPage />}
                      />
                      {/* 2026-09-04 — 配分の予約 (セッション終了後にワンクリック配分) */}
                      <Route
                        path="/districts/:districtId/verification/plans/new"
                        element={<AssignmentPlanEditPage />}
                      />
                      <Route
                        path="/districts/:districtId/verification/plans/:planId/edit"
                        element={<AssignmentPlanEditPage />}
                      />
                      <Route
                        path="/districts/:districtId/insights-moderation"
                        element={<InsightsModerationPage />}
                      />
                      <Route path="/users" element={<UsersAdminPage />} />
                      <Route path="/mcp-tokens" element={<McpTokensPage />} />
                      <Route path="/operation-logs" element={<OperationLogsPage />} />
                      <Route path="/help" element={<AdminGuidePage />} />
                      <Route
                        path="/masters/ai-analysis-profile"
                        element={<AiAnalysisEditorPage uid={ctx.user?.uid} />}
                      />
                      <Route path="/masters/ai-category" element={<AiCategoryMastersPage />} />
                      <Route path="/masters/ai-output-format" element={<AiOutputFormatsPage />} />
                      <Route
                        path="/masters/street-survey-templates"
                        element={<StreetSurveyTemplatesPage />}
                      />
                      <Route
                        path="/masters/ai-dialogue-settings"
                        element={<AiDialogueSettingsPage />}
                      />
                      <Route
                        path="/masters/analysis-display"
                        element={<AnalysisDisplayConfigsPage />}
                      />
                      {/* 【廃止予定 / MD-3 で nav 非表示化】M6 は dead master (runtime 読込みゼロ)。
                    直 URL アクセスは互換のため残す (既存ブックマーク対策)。詳細は issue 参照。 */}
                      <Route
                        path="/masters/score-dimension"
                        element={<ScoreDimensionMastersPage />}
                      />
                      <Route path="/masters/card-print" element={<CardPrintTemplatesPage />} />
                      <Route path="/masters/survey-preset" element={<SurveyPresetsPage />} />
                      <Route
                        path="/masters/survey-preset/:presetId"
                        element={<SurveyPresetEditPage />}
                      />
                      <Route
                        path="/masters/verification-template"
                        element={<VerificationTemplatePresetsPage />}
                      />
                      <Route
                        path="/masters/verification-template/:presetId"
                        element={<VerificationTemplatePresetEditPage />}
                      />
                      <Route path="/masters/mode-profiles" element={<ModeProfilesPage />} />
                      <Route
                        path="*"
                        element={
                          <Card padding="md">
                            <p className="text-sm text-slate-700">ページが見つかりません。</p>
                            <Link
                              to="/"
                              className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
                            >
                              ホームへ戻る
                            </Link>
                          </Card>
                        }
                      />
                    </Routes>
                  </PageErrorBoundary>
                </AdminLayout>
              )}
            </AdminAuthGate>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
